// Global Instructions Rule Applied!
//
// Consolidated-fund Subscriptions grid. Mirrors FundInvestorsTable's shape
// (cfg-driven columns via the "Fund Group Subscriptions" report, sub-key
// user preferences, ViewToolbar + ViewConfig + TableView with per-currency
// sticky totals), plus the actions: "New Consolidated Commitment" opens the
// New commitment form for this consolidated fund, and per row Edit (the
// Consolidated commitment form, which holds the allocation to the member
// funds) / Delete via a rightmost actions column. The GP column is a switch that saves on change
// (GridSwitchCell): it sets the consolidated commitment and its commitments
// in the member funds. The investor name opens the Investor form, the
// parent account the account form.

import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, Dropdown, Modal } from 'antd'
import { MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../../core/components/ViewConfig/index.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import TableView from '../../../core/components/TableView/TableView.tsx'
import { recordHasTotalLabel, stripTotalTextFromCellNode } from '../../../core/utils/recordRowBold.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import {
  TABLE_ACCOUNTS,
  TABLE_COMMITMENTS,
  TABLE_CONSOLIDATED_COMMITMENTS,
  TABLE_INVESTORS
} from '../../../state_manager/globals.ts'
import type { RootState } from '../../../state_manager/store.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useFreezeLeadingColumns } from '../../../state_manager/hooks/useFreezeLeadingColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../state_manager/utils/reportingColumnCatalog.ts'
import { useFundGroupSubscriptionsReportId } from '../../../state_manager/hooks/useFundGroupSubscriptionsReportId.ts'
import { useFundGroupSubscriptions } from '../hooks/useFundGroupSubscriptions.ts'
import { deleteFundGroupSubscription, saveFundGroupSubscription } from '../services/fundGroupsService.ts'
import GridSwitchCell from '../../../core/components/GridSwitchCell.tsx'
import { fetchAllFundGroupSubscriptionsForExport } from '../../shared/table_grid_export/fetchAllFilteredForExport.ts'
import { useReportingGridExport } from '../../shared/table_grid_export/useReportingGridExport.ts'
import HelpTip from '../../../core/components/HelpTip.tsx'
import type { FundGroupSubscriptionRow } from '../../../state_manager/data_models/fund_group.ts'

interface Props {
  fundGroupId: string | null
}

const GP_COLUMN_KEY = 'fund_group_commitments__is_gp_commitment'
const GP_COLUMN_HELP =
  "The general partner's own commitment. Printed on the ILPA workbook. Sets the primary and satellite commitments."

export function FundGroupSubscriptionsTable({ fundGroupId }: Props): React.ReactElement | null {
  const { openFormInTab } = useTabManagerContext()
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()

  const reportId = useFundGroupSubscriptionsReportId()
  const freezeLeadingColumns = useFreezeLeadingColumns(reportId)
  const reportingConfigs = useSelector((s: RootState) => s.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector((s: RootState) => s.globalConfigs?.reporting_column_defaults ?? [])
  const reportingColumns = useSelector((s: RootState) => s.globalConfigs?.reporting_columns ?? [])

  const {
    data,
    loading,
    filterOptions,
    filterOptionsLoading,
    activeFilters,
    activeSorting,
    hasMore,
    currentPage,
    preferenceRow,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableFilters,
    defaultTableSorting
  } = useFundGroupSubscriptions(fundGroupId)

  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)
  const viewConfigColumns: ViewConfigColumn[] = gridColumns

  const fundGroupName = useSelector(
    (s: RootState) => (s.globalConfigs?.fund_groups ?? []).find((g) => g.id === fundGroupId)?.name ?? 'Consolidated fund'
  )
  const fetchAllForExport = useCallback(
    (token: string | null) =>
      fundGroupId
        ? fetchAllFundGroupSubscriptionsForExport(fundGroupId, activeSorting, activeFilters, token)
        : Promise.resolve({ data: [] }),
    [fundGroupId, activeSorting, activeFilters]
  )
  // per-currency Total rows carry no consolidated commitment id
  const keepCommitmentRow = useCallback(
    (row: Record<string, unknown>) => typeof row.fund_group_commitments__id === 'string',
    []
  )
  const { exportLoading, handleExport } = useReportingGridExport({
    title: `${fundGroupName} Investors`,
    gridColumns,
    fetchAll: fetchAllForExport,
    keepRow: keepCommitmentRow
  })

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'fund_groups' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    const rows = getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const { columns: baseColumns, frozenColumns } = useMemo(
    () => createTableColumns({ columns: gridColumns, freezeLeadingColumns, fallbackRecordModuleKey: 'companies' }),
    [gridColumns, freezeLeadingColumns]
  )

  const firstColumnValueKey = gridColumns[0]?.valueKey

  const handleDelete = useCallback(
    (row: FundGroupSubscriptionRow) => {
      modalApi.confirm({
        title: 'Delete consolidated commitment?',
        content: `${row.investors__name ?? ''} — ${row.fund_group_commitments__amount_fmt ?? ''}. Its commitments in the member funds are removed with it.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          const token = getAccessToken(store.getState().auth)
          const res = await deleteFundGroupSubscription(String(row.fund_group_commitments__id ?? ''), token)
          if (res.success) {
            toast.success('Consolidated commitment deleted')
            refetch()
          } else {
            showErrorToast(res.error || 'Failed to delete consolidated commitment')
          }
        }
      })
    },
    [modalApi, refetch]
  )

  // the GP switch: saved on change. A payload of id + is_gp_commitment sets
  // the consolidated commitment and every pocket, and nothing else
  const saveGpCommitment = useCallback(
    async (subscriptionId: string, next: boolean): Promise<boolean> => {
      const res = await saveFundGroupSubscription(
        { id: subscriptionId, is_gp_commitment: next },
        false,
        getAccessToken(store.getState().auth)
      )
      if (!res.success) {
        showErrorToast(res.error || 'Failed to save the GP commitment')
        return false
      }
      toast.success(next ? 'Marked as the GP commitment' : 'No longer the GP commitment')
      refetch()
      return true
    },
    [refetch]
  )

  const columns = useMemo(() => {
    const mapped = baseColumns.map((col) => {
      if (col.key === GP_COLUMN_KEY) {
        return {
          ...col,
          align: 'center' as const,
          title: (
            <span>
              {col.title}
              <HelpTip title={GP_COLUMN_HELP} />
            </span>
          ),
          render: (_: unknown, record: Record<string, unknown>) => {
            const subscriptionId =
              typeof record.fund_group_commitments__id === 'string' ? record.fund_group_commitments__id : ''
            if (recordHasTotalLabel(record) || !subscriptionId) return null
            return (
              <GridSwitchCell
                checked={record[GP_COLUMN_KEY] === true}
                ariaLabel='GP commitment'
                onSave={(next) => saveGpCommitment(subscriptionId, next)}
              />
            )
          }
        }
      }
      const prevRender = col.render
      if (!prevRender) return col
      const isFirstColumn = firstColumnValueKey != null && col.key === firstColumnValueKey
      // the two record links: the investor name opens the Investor form, the
      // parent account the account form
      const isInvestorCol = col.key === 'investors__name'
      const isParentAccountCol = col.key === 'accounts__name'
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const node = prevRender!(value, record, index, editingContext)
          const isTotalRow = recordHasTotalLabel(record)
          const renderedNode = isTotalRow && !isFirstColumn ? stripTotalTextFromCellNode(node) : node

          if (isTotalRow || (!isParentAccountCol && !isInvestorCol)) return renderedNode

          const rawId = isInvestorCol ? record.investors__id : record.accounts__id
          const linkedId = typeof rawId === 'string' ? rawId : ''
          if (!linkedId) return renderedNode

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() =>
                isInvestorCol
                  ? openFormInTab(TABLE_INVESTORS, linkedId, String(record.investors__name ?? ''))
                  : openFormInTab(TABLE_ACCOUNTS, linkedId, String(record.accounts__name ?? ''))
              }
            >
              {renderedNode}
            </button>
          )
        }
      }
    })

    mapped.push({
      title: '',
      key: '_actions',
      width: 48,
      fixed: 'right' as const,
      render: (_: unknown, record: Record<string, unknown>) => {
        if (recordHasTotalLabel(record)) return null
        const row = record as unknown as FundGroupSubscriptionRow
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'edit', label: 'Edit' },
                { type: 'divider' as const },
                { key: 'delete', label: 'Delete', danger: true }
              ],
              onClick: ({ key }) => {
                if (key === 'edit' && row.fund_group_commitments__id)
                  openFormInTab(
                    TABLE_CONSOLIDATED_COMMITMENTS,
                    row.fund_group_commitments__id,
                    String(row.investors__name ?? '')
                  )
                else if (key === 'delete') handleDelete(row)
              }
            }}
          >
            <Button type='text' size='small' icon={<MoreOutlined />} />
          </Dropdown>
        )
      }
    } as (typeof mapped)[number])

    return mapped
  }, [baseColumns, firstColumnValueKey, openFormInTab, handleDelete, saveGpCommitment])

  // a consolidated commitment added or changed on its form, or an investor
  // renamed on the Investor form (each in its own tab), shows here when the
  // user comes back
  const activateRef = useRefreshOnActivate(() => refetch(), { maxAgeMs: 0 })

  const dataSource = useMemo(
    () =>
      data.map((row, i) => {
        const subscriptionId =
          typeof row.fund_group_commitments__id === 'string' ? row.fund_group_commitments__id : ''
        const totalKey = `total-${typeof row.currency_code === 'string' ? row.currency_code : i}`
        const id = subscriptionId || totalKey
        return { ...row, id, key: id }
      }),
    [data]
  )

  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  if (!fundGroupId) return null

  return (
    <div ref={activateRef} className='flex-1 min-h-0 h-full flex flex-col gap-2'>
      {modalContextHolder}
      <ViewToolbar
        moduleKey={TABLE_ACCOUNTS}
        recordTitleField='investors__name'
        addButtonEnabled={false}
        searchEnabled={false}
        variant='secondary'
        leftSlot={
          <div className='flex items-center gap-2 text-white'>
            <span className='w-2 h-2 rounded-full bg-indigo-300' />
            <span className='font-semibold text-sm'>Investors</span>
          </div>
        }
        activeFilters={activeFilters}
        onRefreshData={() => refetch()}
        onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
        defaultFilters={defaultTableFilters}
        onResetFilters={handleResetFilters}
        exportEnabled
        onExport={handleExport}
        exportLoading={exportLoading}
        beforeAddSlot={
          <div className='flex items-center gap-2'>
            <ViewConfig
              moduleTitle='Consolidated Fund Investors'
              columns={viewConfigColumns}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              addColumnsCatalog={addColumnsCatalog}
              addColumnsTabLabels={{ primary: 'Consolidated Commitment', related: 'Related' }}
              columnLayout={columnLayout}
              filteringConfig={{
                currentFilters: activeFilters,
                defaultFilters: defaultTableFilters,
                onApplyFilters: async (filters) => {
                  handleSetFilters(filters)
                },
                onResetToDefaults: async () => {
                  handleSetFilters(defaultTableFilters)
                }
              }}
              sortingConfig={{
                currentSorting: activeSorting,
                defaultSorting: defaultTableSorting,
                onApplySorting: async (sorting) => {
                  handleSetSorting(sorting)
                },
                onResetToDefaults: async () => {
                  handleSetSorting(defaultTableSorting)
                }
              }}
            />
            <Button
              type='primary'
              size='small'
              icon={<PlusOutlined />}
              onClick={() => openFormInTab(TABLE_COMMITMENTS, null, 'New Commitment', { fund_group: fundGroupId })}
            >
              New Consolidated Commitment
            </Button>
          </div>
        }
      />

      <div className='flex-1 min-h-0 h-full' style={{ minHeight: 320 }}>
        <TableView
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          hasMore={hasMore}
          currentPage={currentPage}
          currentFilters={currentFilters}
          onFiltersChange={(filters) => {
            const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
            handleSetFilters(filterArray)
          }}
          onSortChange={(sortConfig: any) => {
            if (sortConfig) {
              handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
            } else {
              handleSetSorting([])
            }
          }}
          filterOptions={filterOptions}
          filterOptionsLoading={filterOptionsLoading}
          currentSortConfig={currentSortConfig}
          frozenColumns={frozenColumns}
          totalsMode='sticky-bottom'
          rowClassName={(record) => (recordHasTotalLabel(record) ? 'font-bold' : undefined)}
          headerStyle={{ padding: '5px', minHeight: 36, backgroundColor: '#f5f5f5' }}
        />
      </div>

    </div>
  )
}

export default FundGroupSubscriptionsTable
